import { zoneServing, type DeliveryZone, type LatLng } from '@agos/shared'

/**
 * The zone that serves an order's point - §9.801, owner O148: *"Make sure nová objednávka picks the zone the same way"*.
 *
 * The owner's request of 2026-08-03 was *"zóna by sa mala vybrať automaticky podľa adresy"*, and this answered it with
 * `findZoneForPoint`: the first enabled zone in list order whose **true** circle held the point. The server's rule is
 * the zones whose **flat-degree** area holds the point, settled by `pickZone` - the zone the address names, else the
 * one nearest the depot, else the smallest - and the server re-prices only web orders, so on a phone order this set the
 * fee. On Šurany's 9 654 delivery orders with coordinates the two disagreed on 443. It is `zoneServing` now, the rule
 * the checkout asks too.
 *
 * **`address` is the text the order will be saved with**, because that is what the server's step 3 reads; `depot` is
 * `GET /delivery-zones/depot`'s point, the checkout's. Null for no point, no zones, or no zone serving the point.
 */
export function zoneForPoint(
  point: { lat: number; lng: number } | null | undefined,
  payload: unknown,
  context: { address: string; depot: LatLng | null },
): string | null {
  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return null

  const zones = zonesOf(payload)
  if (zones.length === 0) return null

  return zoneServing({ lat: point.lat, lng: point.lng }, zones, { address: context.address, depot: context.depot })?.id ?? null
}

function point(value: unknown): LatLng | undefined {
  const row = value as Record<string, unknown> | null | undefined
  if (!row || typeof row.lat !== 'number' || typeof row.lng !== 'number') return undefined
  if (!Number.isFinite(row.lat) || !Number.isFinite(row.lng)) return undefined
  return { lat: row.lat, lng: row.lng }
}

/**
 * The wire's zones, narrowed to the geometry.
 *
 * Read off `unknown` rather than the generated `DeliveryZone` because that type has **every field
 * optional** — `id?: string`, `center?: Coordinates`, `radius?: number` — while the shared one, and
 * so `zoneServing`, requires them. A cast between the two would compile and would be a claim
 * the payload does not make.
 *
 * The two filters below are `zoneChoicesOf`'s, deliberately: **a zone this suggests has to be one
 * the picker offers.** The picker skips a row with no usable id and a row somebody switched off, so
 * suggesting either would set `zoneId` to something its `<select>` has no option for — the control
 * would go blank and the delivery fee would come out of the fallback, which is worse than
 * suggesting nothing at all.
 *
 * A row whose `type` is neither shape is dropped for the same reason it would never match:
 * `zoneServing` tests for `'circle'` and `'polygon'` and falls through everything else.
 * `color`, `minOrder` and `tiers` are filled with blanks — the geometry search never reads them,
 * and the fee is `feeCentsFor`'s job from the zone the picker holds.
 */
function zonesOf(payload: unknown): DeliveryZone[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const id = typeof row.id === 'string' && row.id !== '' ? row.id : null
    if (id === null || row.enabled === false) return []
    if (row.type !== 'circle' && row.type !== 'polygon') return []

    return [
      {
        id,
        name: typeof row.name === 'string' ? row.name : id,
        color: '',
        enabled: true,
        type: row.type,
        center: point(row.center),
        radius: typeof row.radius === 'number' ? row.radius : undefined,
        polygon: Array.isArray(row.polygon)
          ? row.polygon.flatMap((corner) => {
              const corners = point(corner)
              return corners === undefined ? [] : [corners]
            })
          : undefined,
        minOrder: 0,
        tiers: [],
      },
    ]
  })
}
